export class AudioPlayer
{
    private readonly audioCtx: AudioContext;
    public readonly sampleRate: number;

    // We queue audio in chunks of 2048 samples to prevent stuttering
    private readonly bufferSize: number = 2048;
    private readonly buffer: Float32Array;

    private bufferIndex: number   = 0;
    private nextAudioTime: number = 0;

    constructor()
    {
        // Initialize the browser's Web Audio API
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        this.audioCtx           = new AudioContextClass();
        this.sampleRate         = this.audioCtx.sampleRate;
        this.buffer             = new Float32Array(this.bufferSize);
    }

    /**
     * Browsers require a user gesture (like a click) before they allow audio to play.
     * Call this when the user clicks your "Start" or "Load ROM" button!
     */
    public resume(): void
    {
        if (this.audioCtx.state === 'suspended') {
            this.audioCtx.resume();
        }
    }

    /**
     * Takes a raw 0-15 sample from the NES APU and pushes it to the speaker buffer.
     */
    public pushSample(sample: number): void
    {
        // Normalize the 0-15 Triangle output to the -1.0 to 1.0 range the browser expects.
        // (Note: We will change this math later when we mix all 5 channels together)
        const normalizedSample = (sample * 2.0) - 1.0;

        this.buffer[this.bufferIndex++] = normalizedSample;

        if (this.bufferIndex >= this.bufferSize) {
            this.flush();
        }
    }

    private flush(): void
    {
        // Create an empty stereo/mono buffer for the Web Audio API
        const audioBuffer = this.audioCtx.createBuffer(1, this.bufferSize, this.sampleRate);

        // Copy our filled Float32Array into the browser's audio buffer
        audioBuffer.getChannelData(0).set(this.buffer);

        // Create a source node to actually play the buffer
        const source = this.audioCtx.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(this.audioCtx.destination);

        const currentTime = this.audioCtx.currentTime;

        // If we fell behind, add a tiny 50ms safety buffer to prevent crackling
        if (this.nextAudioTime < currentTime) {
            this.nextAudioTime = currentTime + 0.05;
        }

        // Schedule the chunk to play exactly when the last chunk finishes
        source.start(this.nextAudioTime);
        this.nextAudioTime += audioBuffer.duration;

        // Reset our internal buffer
        this.bufferIndex = 0;
    }
}
